import { replaceEqualDeep, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { SpreadSessionView } from '@creativitas/shared';

/**
 * Состояние МАК-сессии не откатывается назад.
 *
 * Страница получает сессию из нескольких источников: снимки живого соединения,
 * ответы на собственные действия, перезапросы при возврате на вкладку и опрос,
 * пока связи нет. Сервер собирает и отправляет их асинхронно, и когда участники
 * тянут карты одновременно, более старое состояние может прийти позже нового —
 * карта на столе исчезала бы и появлялась снова, а ведущий получал бы повторное
 * сообщение о ней.
 *
 * Порядок задаёт `stateVersion`: сервер увеличивает его при каждом изменении
 * сессии. Состояние с меньшим номером, чем уже показанное, отбрасывается.
 * С тем же номером — принимается: «в сети» меняется без нового номера, и
 * свежий снимок присутствия не должен теряться.
 */
export function newerSession(
  current: SpreadSessionView | undefined,
  incoming: SpreadSessionView,
): SpreadSessionView {
  if (current && current.id === incoming.id && incoming.stateVersion < current.stateVersion) {
    return current;
  }
  return incoming;
}

/**
 * Запись в кеш для снимков и ответов на действия — единственная дорога, по
 * которой они меняют экран.
 *
 * `merge` нужен ведущему: общий снимок комнаты приходит без ссылки-приглашения,
 * и без слияния она пропадала бы из шапки после каждого чужого действия.
 */
export function applySession(
  queryClient: QueryClient,
  key: QueryKey,
  incoming: SpreadSessionView,
  merge?: (current: SpreadSessionView | undefined, incoming: SpreadSessionView) => SpreadSessionView,
): void {
  queryClient.setQueryData<SpreadSessionView>(key, (current) => {
    if (newerSession(current, incoming) === current) return current;
    return merge ? merge(current, incoming) : incoming;
  });
}

/** Ведущему: приглашение из прежнего состояния, если в пришедшем его нет. */
export const keepInvite = (
  current: SpreadSessionView | undefined,
  incoming: SpreadSessionView,
): SpreadSessionView => ({
  ...incoming,
  inviteToken: incoming.inviteToken ?? current?.inviteToken ?? null,
});

/**
 * Для `useQuery({ structuralSharing })`: ответ на перезапрос, собранный сервером
 * раньше, чем пришёл последний снимок, не перетирает более новое состояние.
 * Остальное — обычное структурное совпадение React Query, чтобы экран не
 * перерисовывался без изменений.
 */
export function keepNewestSession(oldData: unknown, newData: unknown): unknown {
  const current = oldData as SpreadSessionView | undefined;
  const chosen = newerSession(current, newData as SpreadSessionView);
  if (chosen === current) return current;
  return replaceEqualDeep(oldData, chosen);
}
